<template>
  <!-- Duplicate Management Modal -->
  <div class="modal fade" id="duplicateManagerModal" tabindex="-1" role="dialog" aria-labelledby="duplicateManagerModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="duplicateManagerModalLabel">
            <i class="bx bx-duplicate text-warning me-2"></i>
            Business Duplicate Manager
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <!-- Search and Filter Section -->
          <div class="row mb-4">
            <div class="col-md-8">
              <div class="input-group">
                <span class="input-group-text">
                  <i class="bx bx-search"></i>
                </span>
                <input
                    v-model="searchTerm"
                    type="text"
                    class="form-control"
                    placeholder="Search by business name..."
                >
              </div>
            </div>
            <div class="col-md-4">
              <button @click="findDuplicates" class="btn btn-primary w-100" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                <i v-else class="bx bx-refresh me-2"></i>
                {{ loading ? 'Scanning...' : 'Scan for Duplicates' }}
              </button>
            </div>
          </div>

          <!-- Statistics -->
          <div class="row mb-4" v-if="duplicateGroups.length > 0">
            <div class="col-md-4">
              <div class="card bg-warning bg-opacity-10 border-warning">
                <div class="card-body text-center">
                  <h4 class="text-warning mb-1">{{ duplicateGroups.length }}</h4>
                  <p class="mb-0 text-muted">Duplicate Groups</p>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card bg-danger bg-opacity-10 border-danger">
                <div class="card-body text-center">
                  <h4 class="text-danger mb-1">{{ totalDuplicateRecords }}</h4>
                  <p class="mb-0 text-muted">Total Duplicate Records</p>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card bg-success bg-opacity-10 border-success">
                <div class="card-body text-center">
                  <h4 class="text-success mb-1">{{ selectedForMerge.length }}</h4>
                  <p class="mb-0 text-muted">Selected for Merge</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Scanning for duplicate businesses...</p>
          </div>

          <!-- No Duplicates Found -->
          <div v-else-if="duplicateGroups.length === 0 && !loading && hasScanned" class="text-center py-5">
            <img src="assets/images/no-results.png" alt="No duplicates" class="img-fluid mb-3" style="max-height: 200px;">
            <h4 class="text-success">No Duplicates Found!</h4>
            <p class="text-muted">All business records appear to be unique.</p>
          </div>

          <!-- Duplicate Groups List -->
          <div v-else-if="filteredDuplicateGroups.length > 0" class="duplicate-groups">
            <div v-for="(group, groupIndex) in filteredDuplicateGroups" :key="groupIndex" class="card mb-3">
              <div class="card-header d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <div class="form-check me-3">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        :id="'group-' + groupIndex"
                        @change="toggleGroupSelection(group, $event.target.checked)"
                        :checked="isGroupSelected(group)"
                    >
                    <label class="form-check-label" :for="'group-' + groupIndex"></label>
                  </div>
                  <div>
                    <h6 class="mb-1">
                      <i class="bx bx-store text-primary me-2"></i>
                      {{ group.businessName }}
                    </h6>
                    <small class="text-muted">
                      <i class="bx bx-map-pin me-1"></i>
                      {{ group.wardName }} |
                      <span class="badge bg-warning text-dark">{{ group.records.length }} duplicates</span>
                    </small>
                  </div>
                </div>
                <button
                    @click="toggleGroupDetails(groupIndex)"
                    class="btn btn-outline-primary btn-sm"
                >
                  <i :class="group.showDetails ? 'bx bx-chevron-up' : 'bx bx-chevron-down'"></i>
                  {{ group.showDetails ? 'Hide' : 'Show' }} Details
                </button>
              </div>

              <!-- Group Details -->
              <div v-show="group.showDetails" class="card-body">
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead class="table-light">
                    <tr>
                      <th>
                        <div class="form-check">
                          <input
                              class="form-check-input"
                              type="checkbox"
                              @change="toggleAllRecordsInGroup(group, $event.target.checked)"
                              :checked="areAllRecordsSelected(group)"
                              :indeterminate="isSomeRecordsSelected(group)"
                          >
                        </div>
                      </th>
                      <th>Business ID</th>
                      <th>Registration Date</th>
                      <th>Created By</th>
                      <th>Contact Person</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr
                        v-for="record in group.records"
                        :key="record.businessID"
                        :class="{ 'table-success': record.isLatest, 'table-warning': isRecordSelected(record) }"
                    >
                      <td>
                        <div class="form-check">
                          <input
                              class="form-check-input"
                              type="checkbox"
                              :value="record.businessID"
                              @change="toggleRecordSelection(record, $event.target.checked)"
                              :checked="isRecordSelected(record)"
                          >
                        </div>
                      </td>
                      <td>
                        <div class="d-flex align-items-center">
                          <strong>{{ record.businessID }}</strong>
                          <span v-if="record.isLatest" class="badge bg-success ms-2">Latest</span>
                        </div>
                      </td>
                      <td>{{ formatDate(record.dateCreated) }}</td>
                      <td>{{ record.createdBy }}</td>
                      <td>{{ record.contactPersonNames || '-' }}</td>
                      <td>{{ record.contactPersonPhone || '-' }}</td>
                      <td>
                          <span :class="getStatusBadgeClass(record.renewalStatus)">
                            {{ record.renewalStatus || 'PENDING' }}
                          </span>
                      </td>
                      <td>
                        <div class="btn-group btn-group-sm">
                          <button @click="viewBusinessDetails(record)" class="btn btn-outline-primary btn-sm">
                            <i class="bx bx-eye"></i>
                          </button>
                          <button @click="editBusiness(record)" class="btn btn-outline-secondary btn-sm">
                            <i class="bx bx-edit"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Merge Actions for this group -->
                <div v-if="getSelectedRecordsInGroup(group).length > 1" class="mt-3 p-3 bg-light rounded">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 class="mb-1">Merge {{ getSelectedRecordsInGroup(group).length }} records</h6>
                      <small class="text-muted">
                        Keep: <strong>{{ getLatestRecord(group).businessID }}</strong> ({{ formatDate(getLatestRecord(group).dateCreated) }})
                      </small>
                    </div>
                    <button
                        @click="mergeGroupRecords(group)"
                        class="btn btn-warning btn-sm"
                        :disabled="merging"
                    >
                      <span v-if="merging" class="spinner-border spinner-border-sm me-2"></span>
                      <i v-else class="bx bx-merge me-2"></i>
                      Merge Selected
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer" v-if="duplicateGroups.length > 0">
          <div class="d-flex justify-content-between w-100">
            <div class="d-flex align-items-center">
              <div class="form-check me-3">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="selectAllGroups"
                    @change="toggleAllGroups($event.target.checked)"
                    :checked="allGroupsSelected"
                >
                <label for="selectAllGroups" class="form-check-label me-3">Select All Groups</label>
              </div>
              <span class="text-muted">{{ selectedForMerge.length }} records selected</span>
            </div>
            <div>
              <button @click="showMergeHistory" class="btn btn-outline-info me-2">
                <i class="bx bx-history"></i>
                View Merge History
              </button>
              <button
                  @click="mergeAllSelected"
                  class="btn btn-success"
                  :disabled="selectedForMerge.length === 0 || merging"
                  v-if="selectedForMerge.length > 0"
              >
                <span v-if="merging" class="spinner-border spinner-border-sm me-2"></span>
                <i v-else class="bx bx-merge me-2"></i>
                Merge All Selected ({{ selectedForMerge.length }})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Merge History Modal -->
  <div class="modal fade" id="mergeHistoryModal" tabindex="-1" role="dialog" aria-labelledby="mergeHistoryModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="mergeHistoryModalLabel">
            <i class="bx bx-history text-info me-2"></i>
            Merge History
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <!-- History Search -->
          <div class="row mb-3">
            <div class="col-md-8">
              <input
                  v-model="historySearch"
                  type="text"
                  class="form-control"
                  placeholder="Search merge history..."
              >
            </div>
            <div class="col-md-4">
              <button @click="loadMergeHistory" class="btn btn-outline-primary w-100" :disabled="loadingHistory">
                <span v-if="loadingHistory" class="spinner-border spinner-border-sm me-2"></span>
                <i v-else class="bx bx-refresh me-2"></i>
                Refresh
              </button>
            </div>
          </div>

          <!-- History Loading -->
          <div v-if="loadingHistory" class="text-center py-4">
            <div class="spinner-border text-primary"></div>
            <p class="mt-2 text-muted">Loading merge history...</p>
          </div>

          <!-- No History -->
          <div v-else-if="mergeHistory.length === 0" class="text-center py-4">
            <i class="bx bx-history display-4 text-muted"></i>
            <h5 class="mt-3">No Merge History Found</h5>
            <p class="text-muted">No businesses have been merged yet.</p>
          </div>

          <!-- History List -->
          <div v-else class="history-list">
            <div v-for="(historyItem, index) in filteredMergeHistory" :key="index" class="card mb-3">
              <div class="card-header d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-1">
                    <i class="bx bx-merge text-success me-2"></i>
                    {{ historyItem.businessName }}
                  </h6>
                  <small class="text-muted">
                    Merged on {{ formatDate(historyItem.mergeDate) }} by {{ historyItem.mergedBy }}
                  </small>
                </div>
                <div>
                  <span class="badge bg-info me-2">{{ historyItem.mergedRecords.length }} records merged</span>
                  <button @click="toggleHistoryDetails(index)" class="btn btn-outline-primary btn-sm">
                    <i :class="historyItem.showDetails ? 'bx bx-chevron-up' : 'bx bx-chevron-down'"></i>
                  </button>
                </div>
              </div>

              <div v-show="historyItem.showDetails" class="card-body">
                <div class="row mb-3">
                  <div class="col-md-6">
                    <div class="card bg-success bg-opacity-10 border-success">
                      <div class="card-body">
                        <h6 class="text-success">
                          <i class="bx bx-check-circle me-2"></i>
                          Kept Record
                        </h6>
                        <p class="mb-1"><strong>Business ID:</strong> {{ historyItem.keptRecord.businessID }}</p>
                        <p class="mb-1"><strong>Created:</strong> {{ formatDate(historyItem.keptRecord.dateCreated) }}</p>
                        <p class="mb-0"><strong>By:</strong> {{ historyItem.keptRecord.createdBy }}</p>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="card bg-danger bg-opacity-10 border-danger">
                      <div class="card-body">
                        <h6 class="text-danger">
                          <i class="bx bx-x-circle me-2"></i>
                          Merged Records ({{ historyItem.mergedRecords.length }})
                        </h6>
                        <div class="merged-records-list" style="max-height: 200px; overflow-y: auto;">
                          <div v-for="record in historyItem.mergedRecords" :key="record.businessID" class="d-flex justify-content-between align-items-center py-1 border-bottom">
                            <div>
                              <small><strong>{{ record.businessID }}</strong></small><br>
                              <small class="text-muted">{{ formatDate(record.dateCreated) }}</small>
                            </div>
                            <button @click="restoreRecord(record, historyItem)" class="btn btn-outline-warning btn-sm">
                              <i class="bx bx-undo"></i>
                              Restore
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Restore Actions -->
                <div class="d-flex justify-content-end">
                  <button @click="restoreAllMergedRecords(historyItem)" class="btn btn-warning me-2" :disabled="restoring">
                    <span v-if="restoring" class="spinner-border spinner-border-sm me-2"></span>
                    <i v-else class="bx bx-undo me-2"></i>
                    Restore All Merged Records
                  </button>
                  <button @click="deleteMergeHistory(historyItem)" class="btn btn-outline-danger" :disabled="deleting">
                    <span v-if="deleting" class="spinner-border spinner-border-sm me-2"></span>
                    <i v-else class="bx bx-trash me-2"></i>
                    Delete History
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Business Details Modal -->
  <div class="modal fade" id="businessDetailsModal" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bx bx-store me-2"></i>
            Business Details - {{ selectedBusiness.businessID }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="row" v-if="selectedBusiness">
            <div class="col-md-6">
              <table class="table table-borderless">
                <tr>
                  <th>Business Name:</th>
                  <td>{{ selectedBusiness.businessName }}</td>
                </tr>
                <tr>
                  <th>Business ID:</th>
                  <td>{{ selectedBusiness.businessID }}</td>
                </tr>
                <tr>
                  <th>Sub County:</th>
                  <td>{{ selectedBusiness.subCountyName }}</td>
                </tr>
                <tr>
                  <th>Ward:</th>
                  <td>{{ selectedBusiness.wardName }}</td>
                </tr>
                <tr>
                  <th>Physical Address:</th>
                  <td>{{ selectedBusiness.physicalAddress || '-' }}</td>
                </tr>
                <tr>
                  <th>Plot Number:</th>
                  <td>{{ selectedBusiness.plotNumber || '-' }}</td>
                </tr>
              </table>
            </div>
            <div class="col-md-6">
              <table class="table table-borderless">
                <tr>
                  <th>Contact Person:</th>
                  <td>{{ selectedBusiness.contactPersonNames || '-' }}</td>
                </tr>
                <tr>
                  <th>Phone:</th>
                  <td>{{ selectedBusiness.contactPersonPhone || '-' }}</td>
                </tr>
                <tr>
                  <th>Email:</th>
                  <td>{{ selectedBusiness.contactPersonEmail || '-' }}</td>
                </tr>
                <tr>
                  <th>Created By:</th>
                  <td>{{ selectedBusiness.createdBy }}</td>
                </tr>
                <tr>
                  <th>Date Created:</th>
                  <td>{{ formatDate(selectedBusiness.dateCreated) }}</td>
                </tr>
                <tr>
                  <th>Status:</th>
                  <td>
                    <span :class="getStatusBadgeClass(selectedBusiness.renewalStatus)">
                      {{ selectedBusiness.renewalStatus || 'PENDING' }}
                    </span>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {execute, trade} from "@/api";
import moment from 'moment';

export default {
  name: "DuplicateManagerModal",
  data() {
    return {
      loading: false,
      merging: false,
      restoring: false,
      deleting: false,
      loadingHistory: false,
      hasScanned: false,
      searchTerm: '',
      historySearch: '',

      duplicateGroups: [],
      selectedForMerge: [],
      mergeHistory: [],
      selectedBusiness: {},

      // API endpoints (these should match your API structure)
      apiEndpoint: 'trade' // or whatever your endpoint is
    }
  },

  computed: {
    filteredDuplicateGroups() {
      if (!this.searchTerm) return this.duplicateGroups;
      return this.duplicateGroups.filter(group =>
          group.businessName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          group.wardName.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    },

    filteredMergeHistory() {
      if (!this.historySearch) return this.mergeHistory;
      return this.mergeHistory.filter(item =>
          item.businessName.toLowerCase().includes(this.historySearch.toLowerCase()) ||
          item.mergedBy.toLowerCase().includes(this.historySearch.toLowerCase())
      );
    },

    totalDuplicateRecords() {
      return this.duplicateGroups.reduce((total, group) => total + group.records.length, 0);
    },

    allGroupsSelected() {
      return this.duplicateGroups.length > 0 && this.duplicateGroups.every(group =>
          group.records.every(record => this.isRecordSelected(record))
      );
    }
  },

  methods: {
    // Find duplicates based on businessName and wardName
    async findDuplicates() {
      this.loading = true;
      this.hasScanned = true;
      this.duplicateGroups = [];

      try {
        const data = new FormData();
        data.append("function", "findDuplicateBusinesses");
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

        const response = await execute(data, trade);

        if (response.data.success) {
          this.duplicateGroups = this.processDuplicateData(response.data.data.duplicates);
        } else {
          this.$emit('show-message', response.data.message, 'error');
        }
      } catch (error) {
        this.$emit('show-message', 'Error finding duplicates: ' + error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    // Process duplicate data into groups
    processDuplicateData(duplicates) {
      const groups = [];

      duplicates.forEach(group => {
        // Sort records by dateCreated (latest first)
        group.records.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

        // Mark the latest record
        if (group.records.length > 0) {
          group.records[0].isLatest = true;
        }

        groups.push({
          ...group,
          showDetails: false,
          records: group.records
        });
      });

      return groups;
    },

    // Selection methods
    toggleRecordSelection(record, checked) {
      if (checked) {
        if (!this.isRecordSelected(record)) {
          this.selectedForMerge.push(record.businessID);
        }
      } else {
        const index = this.selectedForMerge.indexOf(record.businessID);
        if (index > -1) {
          this.selectedForMerge.splice(index, 1);
        }
      }
    },

    isRecordSelected(record) {
      return this.selectedForMerge.includes(record.businessID);
    },

    toggleGroupSelection(group, checked) {
      group.records.forEach(record => {
        this.toggleRecordSelection(record, checked);
      });
    },

    isGroupSelected(group) {
      return group.records.every(record => this.isRecordSelected(record));
    },

    toggleAllRecordsInGroup(group, checked) {
      group.records.forEach(record => {
        this.toggleRecordSelection(record, checked);
      });
    },

    areAllRecordsSelected(group) {
      return group.records.every(record => this.isRecordSelected(record));
    },

    isSomeRecordsSelected(group) {
      const selectedInGroup = group.records.filter(record => this.isRecordSelected(record));
      return selectedInGroup.length > 0 && selectedInGroup.length < group.records.length;
    },

    toggleAllGroups(checked) {
      this.duplicateGroups.forEach(group => {
        this.toggleGroupSelection(group, checked);
      });
    },

    getSelectedRecordsInGroup(group) {
      return group.records.filter(record => this.isRecordSelected(record));
    },

    getLatestRecord(group) {
      return group.records.find(record => record.isLatest) || group.records[0];
    },

    // UI methods
    toggleGroupDetails(groupIndex) {
      this.duplicateGroups[groupIndex].showDetails = !this.duplicateGroups[groupIndex].showDetails;
    },

    toggleHistoryDetails(index) {
      this.$set(this.mergeHistory[index], 'showDetails', !this.mergeHistory[index].showDetails);
    },

    // Merge operations
    async mergeGroupRecords(group) {
      const selectedRecords = this.getSelectedRecordsInGroup(group);
      if (selectedRecords.length < 2) {
        this.$emit('show-message', 'Please select at least 2 records to merge', 'warning');
        return;
      }

      await this.performMerge(selectedRecords, group);
    },

    async mergeAllSelected() {
      if (this.selectedForMerge.length < 2) {
        this.$emit('show-message', 'Please select at least 2 records to merge', 'warning');
        return;
      }

      // Group selected records by business group
      const groupedSelections = {};
      this.duplicateGroups.forEach(group => {
        const selectedInGroup = this.getSelectedRecordsInGroup(group);
        if (selectedInGroup.length > 1) {
          groupedSelections[group.businessName + '-' + group.wardName] = {
            group: group,
            records: selectedInGroup
          };
        }
      });

      // Merge each group separately
      for (const key in groupedSelections) {
        await this.performMerge(groupedSelections[key].records, groupedSelections[key].group);
      }
    },

    async performMerge(recordsToMerge, group) {
      this.merging = true;

      try {
        // Find the latest record (the one to keep)
        const latestRecord = recordsToMerge.find(r => r.isLatest) || recordsToMerge[0];
        const recordsToRemove = recordsToMerge.filter(r => r.businessID !== latestRecord.businessID);

        const data = new FormData();
        data.append("function", "mergeBusinessRecords");
        data.append("keepBusinessID", latestRecord.businessID);
        data.append("removeBusinessIDs", JSON.stringify(recordsToRemove.map(r => r.businessID)));
        data.append("businessName", group.businessName);
        data.append("wardName", group.wardName);
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

        const response = await execute(data, trade);

        if (response.data.success) {
          this.$emit('show-message', `Successfully merged ${recordsToRemove.length} records`, 'success');

          // Remove merged records from selection
          recordsToRemove.forEach(record => {
            const index = this.selectedForMerge.indexOf(record.businessID);
            if (index > -1) {
              this.selectedForMerge.splice(index, 1);
            }
          });

          // Refresh the duplicate list
          await this.findDuplicates();

          // Refresh parent component data if needed
          this.$emit('refresh-data');

        } else {
          this.$emit('show-message', response.data.message, 'error');
        }
      } catch (error) {
        this.$emit('show-message', 'Error merging records: ' + error.message, 'error');
      } finally {
        this.merging = false;
      }
    },

    // History operations
    async loadMergeHistory() {
      this.loadingHistory = true;

      try {
        const data = new FormData();
        data.append("function", "getMergeHistory");
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

        const response = await execute(data, trade);

        if (response.data.success) {
          this.mergeHistory = response.data.data.history.map(item => ({
            ...item,
            showDetails: false
          }));
        } else {
          this.$emit('show-message', response.data.message, 'error');
        }
      } catch (error) {
        this.$emit('show-message', 'Error loading merge history: ' + error.message, 'error');
      } finally {
        this.loadingHistory = false;
      }
    },

    showMergeHistory() {
      this.loadMergeHistory();
      const historyModal = new bootstrap.Modal(document.getElementById('mergeHistoryModal'));
      historyModal.show();
    },

    async restoreRecord(record, historyItem) {
      this.restoring = true;

      try {
        const data = new FormData();
        data.append("function", "restoreBusinessRecord");
        data.append("businessID", record.businessID);
        data.append("mergeHistoryId", historyItem.id);
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

        const response = await execute(data,trade);

        if (response.data.success) {
          this.$emit('show-message', `Record ${record.businessID} restored successfully`, 'success');
          await this.loadMergeHistory();
          this.$emit('refresh-data');
        } else {
          this.$emit('show-message', response.data.message, 'error');
        }
      } catch (error) {
        this.$emit('show-message', 'Error restoring record: ' + error.message, 'error');
      } finally {
        this.restoring = false;
      }
    },

    async restoreAllMergedRecords(historyItem) {
      this.restoring = true;

      try {
        const data = new FormData();
        data.append("function", "restoreAllMergedRecords");
        data.append("mergeHistoryId", historyItem.id);
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

        const response = await execute(data, trade);

        if (response.data.success) {
          this.$emit('show-message', `All ${historyItem.mergedRecords.length} records restored successfully`, 'success');
          await this.loadMergeHistory();
          this.$emit('refresh-data');
        } else {
          this.$emit('show-message', response.data.message, 'error');
        }
      } catch (error) {
        this.$emit('show-message', 'Error restoring records: ' + error.message, 'error');
      } finally {
        this.restoring = false;
      }
    },

    async deleteMergeHistory(historyItem) {
      if (!confirm('Are you sure you want to delete this merge history? This action cannot be undone.')) {
        return;
      }

      this.deleting = true;

      try {
        const data = new FormData();
        data.append("function", "deleteMergeHistory");
        data.append("mergeHistoryId", historyItem.id);
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

        const response = await execute(data, trade);

        if (response.data.success) {
          this.$emit('show-message', 'Merge history deleted successfully', 'success');
          await this.loadMergeHistory();
        } else {
          this.$emit('show-message', response.data.message, 'error');
        }
      } catch (error) {
        this.$emit('show-message', 'Error deleting history: ' + error.message, 'error');
      } finally {
        this.deleting = false;
      }
    },

    // Business details
    viewBusinessDetails(business) {
      this.selectedBusiness = business;
      const detailsModal = new bootstrap.Modal(document.getElementById('businessDetailsModal'));
      detailsModal.show();
    },

    editBusiness(business) {
      this.$emit('edit-business', business);
    },

    // Utility methods
    formatDate(date) {
      return moment(date).format('DD MMM YYYY, HH:mm A');
    },

    getStatusBadgeClass(status) {
      const statusClasses = {
        'PENDING': 'badge bg-warning',
        'APPROVED': 'badge bg-success',
        'REJECTED': 'badge bg-danger',
        'INVOICED': 'badge bg-info',
        'PAID': 'badge bg-primary'
      };
      return statusClasses[status] || 'badge bg-secondary';
    },

    // Public methods for parent component
    openModal() {
      const modal = new bootstrap.Modal(document.getElementById('duplicateManagerModal'));
      modal.show();
    },

    closeModal() {
      const modal = bootstrap.Modal.getInstance(document.getElementById('duplicateManagerModal'));
      if (modal) {
        modal.hide();
      }
    }
  }
}
</script>

<style scoped>
.duplicate-groups {
  max-height: 600px;
  overflow-y: auto;
}

.history-list {
  max-height: 500px;
  overflow-y: auto;
}

.merged-records-list {
  max-height: 200px;
  overflow-y: auto;
}

.table-success {
  background-color: rgba(25, 135, 84, 0.1) !important;
}

.table-warning {
  background-color: rgba(255, 193, 7, 0.1) !important;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

.card-header {
  background-color: #f8f9fa;
}

.badge {
  font-size: 0.75em;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>